import { fixedBottomShellClasses, useFixedBottomSpacer } from "@ui/hooks/use-fixed-bottom-spacer";
import { tv } from "@ui/lib/tv";
import type { ReactNode } from "react";

const OVERFLOW_SHADOW = "0 -14px 14px -7px rgba(74, 74, 74, 0.12)";
const OVERFLOW_STYLE = { boxShadow: OVERFLOW_SHADOW };

const dock = tv({
  defaultVariants: {
    hasOverflow: false,
  },
  slots: {
    root: [fixedBottomShellClasses, "bg-white pb-safe border-t border-transparent transition-shadow duration-300"],
    container: "p-4 md:p-5 lg:p-8",
  },
  variants: {
    hasOverflow: {
      false: {},
      true: {
        root: "border-t-neutral-200",
      },
    },
  },
});

export interface DockProps {
  children: ReactNode;
  /** Optional content above the main dock area (e.g., progress bar) */
  topAccessory?: ReactNode;
  /** Whether page content overflows behind the dock — shows shadow/border when true */
  hasOverflow?: boolean;
  className?: string;
}

/**
 * Fixed-position viewport container that sticks to the bottom of the screen.
 * Provides overflow-aware shadow/border, safe-area insets for mobile, and a
 * spacer to prevent content from being hidden behind the dock.
 * Pair with ButtonDock for action-button layouts.
 */
export function Dock({ children, topAccessory, hasOverflow = false, className }: DockProps) {
  const { hasOverflow: showsOverflow, ref, spacer } = useFixedBottomSpacer<HTMLDivElement>({ hasOverflow });

  const styles = dock({ hasOverflow: showsOverflow });

  return (
    <>
      <div ref={ref} className={styles.root({ className })} style={showsOverflow ? OVERFLOW_STYLE : undefined}>
        {topAccessory}
        <div className={styles.container()}>{children}</div>
      </div>
      {spacer}
    </>
  );
}
